import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import "@/lib/monitoring"; // Init Sentry FIRST so boot errors are captured
import { Sentry } from "@/lib/monitoring";
import App from "@/App";
import { I18nProvider } from "@/lib/i18n.jsx";
import { installArabicNumeralNormalizer } from "@/lib/arabicNumerals";

// Silently convert Arabic (٠-٩) / Persian (۰-۹) digits typed into any
// <input> or <textarea> into ASCII (0-9) BEFORE React's synthetic
// onChange fires, so validators + backend contracts never see the
// non-ASCII glyphs. One-time capture-phase listener on document.
installArabicNumeralNormalizer();

// Take full control of scroll restoration. The browser's default "auto"
// mode competes with our per-list scroll hooks and, on slow mobile networks,
// tends to clobber our restore attempt just as a lazy-loaded chunk mounts —
// which is what caused the "back from recipe detail scrolls to the top of
// وصفات المنصة" bug users reported on Android Chrome.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

// ── Global network-error safety net ───────────────────────────────────────
// A handful of pages call axios without a local try/catch. When one of
// those hiccups (a 404 on a stale job id, a 502 during a rolling deploy)
// it bubbles up as an unhandled promise rejection — and in dev-mode CRA
// renders its bright red overlay on top of the app. We swallow those
// axios rejections here so the UI stays clean; the axios interceptor
// already logs them via console.warn.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    const err = event.reason;
    const isAxios = err && (err.isAxiosError === true || (err.config && err.request));
    if (isAxios) {
      // eslint-disable-next-line no-console
      console.warn(
        "[network] swallowed axios error:",
        err?.response?.status,
        err?.config?.method?.toUpperCase(),
        err?.config?.url,
      );
      event.preventDefault();
    }
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div dir="rtl" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", background: "#f9fafb", fontFamily: "system-ui" }}>
          <div style={{ maxWidth: 400, textAlign: "center", background: "white", padding: 32, borderRadius: 24, boxShadow: "0 10px 30px -10px rgba(0,0,0,0.1)" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>😔</div>
            <h2 style={{ fontWeight: 900, marginBottom: 8 }}>حصل خطأ غير متوقع</h2>
            <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 20, lineHeight: 1.8 }}>
              ما تقلق — تم إبلاغ فريقنا تلقائياً وحنصلحها بأسرع وقت. جرّب تعيد المحاولة.
            </p>
            <button onClick={resetError} style={{ background: "linear-gradient(135deg,#ff6b00,#ff8533)", color: "white", border: "none", padding: "12px 28px", borderRadius: 999, fontWeight: 700, cursor: "pointer" }}>
              حاول مرة ثانية
            </button>
          </div>
        </div>
      )}
    >
      <I18nProvider>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </I18nProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

// Register the PWA service worker (handles Web Push notifications).
// Registered in BOTH dev and production because we test push flows
// in the preview environment. The SW itself (see public/sw.js) is
// minimal — no fetch caching, only push + notificationclick handlers —
// so it never risks serving stale bundles during development.
if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .catch((err) => console.warn("SW register failed", err));
  });
}
